import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import {metrics} from '~/vendor/metrics';
import {
  AlertWarningIcon,
  CheckIcon,
  Button,
  Link
} from '@customink/pigment-react';

// selectors
import {getAnchorStyle} from '~/selectors/ProductSelectors';
import {anchorHasWoodenMaterial} from '~/selectors/ProductSelectors';

import renderInlineSvg from '~/utils/renderInlineSvg';
import {closeIcon, lightbulb} from '~/constants/InlineSvgs';

import oneColorDesignImg from '~/assets/images/laserengraving/one_color_design.png';
import woodenOneColorDesignImg from '~/assets/images/laserengraving/wooden_one_color_design.png';
import gradientDesignImg from '~/assets/images/laserengraving/gradient_design.png';
import woodenGradientDesignImg from '~/assets/images/laserengraving/wooden_gradient_design.png';

import './index.scss';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const LaserEngravedModal = ({dismiss}) => {
  const navigate = useNavigate();
  const product = useSelector(getAnchorStyle);
  const hasWoodenMaterial = useSelector(anchorHasWoodenMaterial);
  const oneColorImageAlt = hasWoodenMaterial ? 'wooden-one-color' : 'one-color';
  const gradientImageAlt = hasWoodenMaterial ? 'wooden-gradient' : 'gradient';
  const oneColorDesignImage = hasWoodenMaterial
    ? woodenOneColorDesignImg
    : oneColorDesignImg;
  const gradientDesignImage = hasWoodenMaterial
    ? woodenGradientDesignImg
    : gradientDesignImg;

  useEffect(() => {
    metrics.pageViewTag('/ndx/designtip/laserengraving');
  }, []);

  const handleProductLinkClicked = () => {
    metrics.pageViewTag('/ndx/designtip/laserengraving/browseproducts');
    dismiss();
    navigate(
      `/productCatalog/${product.primary_category_name}/${product.primary_category_id}`
    );
  };

  const handleCloseClicked = () => {
    metrics.pageViewTag('/ndx/designtip/laserengraving/close');
    dismiss();
  };

  const handleDoneClicked = () => {
    metrics.pageViewTag('/ndx/designtip/laserengraving/confirm');
    dismiss();
  };

  const handleMouseDown = e => {
    e.stopPropagation();
  };

  const handleHelpClicked = () => {
    metrics.pageViewTag('/ndx/designtip/laserengraving/help');
    dismiss();
    navigate('/help');
  };

  return (
    <div className="ndx-LaserEngravedModal" onMouseDown={handleMouseDown}>
      <div
        className="ndx-LaserEngravedModal-close"
        onClick={handleCloseClicked}
        dangerouslySetInnerHTML={renderInlineSvg(closeIcon)}
        role="button"
        data-testid="close-button"
      />
      <div className="ndx-LaserEngravedModal-content">
        <h1>Design Tips for Laser Engraved Products</h1>
        <p className="ndx-LaserEngravedModal-content-summary">
          Laser engraving is done on the surface of your product using no ink
          colors. Your final engraved design may appear lighter or darker than
          what is shown. Stick to these recommendations when adding your design
          or&nbsp;<Link onClick={handleHelpClicked}>reach out for help</Link>.
        </p>
        <div className="ndx-LaserEngravedModal-content-imgs">
          <div className="ndx-LaserEngravedModal-content-imgs-container">
            <div className="ndx-LaserEngravedModal-content-imgs-container-header">
              <CheckIcon color="success" />
              <span>One-color artwork</span>
            </div>
            <img src={oneColorDesignImage} alt={oneColorImageAlt} />
          </div>
          <div className="ndx-LaserEngravedModal-content-imgs-container">
            <div className="ndx-LaserEngravedModal-content-imgs-container-header">
              <AlertWarningIcon color="red" />
              <span>Gradients or photos</span>
            </div>
            <img src={gradientDesignImage} alt={gradientImageAlt} />
          </div>
        </div>
        <div className="ndx-LaserEngravedModal-content-tip">
          <span
            className="ndx-LaserEngravedModal-content-tip-icon"
            dangerouslySetInnerHTML={renderInlineSvg(lightbulb)}
          />
          <span>
            Our artists will also adjust your image for the best result.
          </span>
        </div>
        <div className="ndx-LaserEngravedModal-content-cta">
          <Button
            size="large"
            className="ndx-LaserEngravedModal-content-cta-primary"
            onClick={handleDoneClicked}>
            Got It!
          </Button>
          <a
            className="ndx-LaserEngravedModal-content-cta-link"
            onClick={handleProductLinkClicked}>
            Find Similar Products With More Color Options.
          </a>
        </div>
      </div>
    </div>
  );
};

LaserEngravedModal.displayName = 'LaserEngravedModal';
const propTypes = {
  dismiss: PropTypes.func
};

LaserEngravedModal.propTypes = propTypes;

export default LaserEngravedModal;
